import { datadogRum } from '@datadog/browser-rum'; // TODO: SAV-3878 build react-native support
import { isNil } from 'es-toolkit';

import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { useRoutePattern } from '@savvy/deprecated-shared/hooks/use-route-pattern';
import { useRouter } from '@savvy/deprecated-shared/hooks/use-router';
import { useMutationWithErrorLogging } from '@savvy/deprecated-shared/hooks/useMutationWithErrorLogging';

import type { TrackEventInput } from '../gql/graphql';
import type { TrackableRoute } from './utils';
import { useHousehold } from '../contexts/household-context';
import { mapRouteToPageName } from './utils';

const UseTrackEventMutation = tadaGraphql(/* GraphQL */ `
  mutation UseTrackEvent($input: TrackEventInput!) {
    trackEvent(input: $input) {
      __typename
    }
  }
`);

export const BEACON_FAILURE_ACTION = 'analytics_beacon_failed';

/**
 * A dropped analytics beacon breaks nothing the user sees, so it must not reach
 * RUM as an error: inside a click's activity window it tags that click
 * `error_click` and counts against the product-quality KR. Reported as a RUM
 * action instead, so delivery stays observable without inflating the numerator.
 *
 * `console.warn` rather than `console.error` on purpose — RUM turns a
 * `console.error` into an error event, which is the thing being avoided.
 */
export function reportBeaconFailure(error: { readonly message: string }): void {
  try {
    datadogRum.addAction(BEACON_FAILURE_ACTION, { error_message: error.message });
  } catch (rumError) {
    // @datadog/browser-rum is web-only (SAV-3878) and throws on native.
    console.warn('[datadog-rum]', rumError);
  }
}

/**
 * Track an event in MixPanel. Runs asynchronously and has a low chance of
 * failing without retries due to using raw `POST` under-the-hood. This is a
 * okay tradeoff for a method we'll be calling relatively frequently that does
 * not need to be 100% reliable
 *
 * @param event - The event to track
 */
export function useTrackEvent<Event extends keyof TrackEventInput>(
  event: Event,
): (data: Omit<NonNullable<TrackEventInput[Event]>, 'pageName' | 'route' | 'urlQueryParams'>) => void {
  const { household } = useHousehold();
  const router = useRouter();
  // `onError` also stops Apollo rejecting the fire-and-forget promise, which
  // would otherwise surface as an unhandled rejection.
  const [mutation] = useMutationWithErrorLogging(
    UseTrackEventMutation,
    { onError: reportBeaconFailure },
    { reportToRum: false },
  );
  const routePattern = useRoutePattern();
  // eslint-disable-next-line typescript/consistent-type-assertions -- FIXME: This rule was recently enabled for the repo
  const pageName = mapRouteToPageName(routePattern as TrackableRoute);
  if (isNil(pageName)) {
    return () => {
      /* empty */
    };
  }

  return (data?: Omit<NonNullable<TrackEventInput[Event]>, 'pageName' | 'route' | 'urlQueryParams'>) => {
    mutation({
      variables: {
        input: {
          [event]: {
            ...data,
            pageName,
            route: router.pathname,
            urlQueryParams: router.query,
            isDemoHousehold: household?.demo ?? false,
          },
        },
      },
    });
  };
}
